import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo, { ORG_JSONLD, WEBSITE_JSONLD } from "@/components/Seo";
import { ArrowRight, Database, Filter, Layout, Check, X } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import RotatingWord from "@/components/RotatingWord";
import CaseCard from "@/components/CaseCard";
import { listCaseStudies } from "@/lib/api";
import { HOME, SCAN } from "@/constants/testIds";

const Stat = ({ num, label }) => (
  <div data-testid={HOME.statBlock}>
    <div className="stat-num">{num}</div>
    <p className="text-sm text-on-dark-muted mt-3 max-w-[220px]">{label}</p>
  </div>
);

const METHOD = [
  {
    icon: Database,
    title: "10 milioni di figure professionali verificate",
    badge: "Database",
    body: "Il nostro canale principale. Identifichiamo i profili più coerenti con la ricerca e li contattiamo direttamente, anche chi non sta cercando lavoro, anche chi non ha mai risposto a un annuncio.",
  },
  {
    icon: Filter,
    title: "Selezione reale. Fatta da recruiter Lovac.",
    badge: "Selezione",
    body: "Ogni persona che ti presentiamo è stata intervistata da un professionista Lovac. L'AI analizza i profili. I recruiter decidono chi vale il tuo tempo.",
  },
  {
    icon: Layout,
    title: "Visibilità totale sul processo",
    badge: "Portale",
    body: "Dashboard in tempo reale, stato della ricerca, schede candidato, aggiornamenti costanti. Sai sempre cosa sta succedendo, senza dover chiedere.",
  },
];

const OLD = [
  "Pubblicano annunci e raccolgono CV in entrata",
  "Ti mandano chi è disponibile, non chi vale",
  "La selezione vera la fai tu",
  "Nessuna visibilità sul processo",
  "Paghi anche se non ottieni risultati",
];
const NEW = [
  "Hunting su 10M di profili verificati, advertising mirato, annunci che portano sul nostro processo, non sulla tua email",
  "Contattano direttamente chi non sta cercando lavoro",
  "Ogni candidato è già stato intervistato da un recruiter Lovac",
  "Dashboard in tempo reale, sai sempre a che punto siamo",
  "Non paghi nulla se non ottieni risultati",
];

const FAQS = [
  {
    q: "Cosa fate che non posso fare da solo?",
    a: "Probabilmente hai già provato LinkedIn. Il problema non è il canale, è il metodo. Lovac usa LinkedIn con strumenti di arricchimento dati che permettono di identificare e contattare direttamente profili selezionati, anche chi non sta cercando lavoro e non risponde mai agli annunci. Poi c'è la parte più difficile: lo screening, i colloqui, la valutazione di motivazione e fit culturale. È un lavoro a tempo pieno. Non è compatibile con il tuo.",
  },
  {
    q: "Cosa vi distingue dalle altre agenzie?",
    a: "Le agenzie tradizionali lavorano su chi si candida, raccolgono CV in entrata e ti mandano chi è disponibile. Noi facciamo il contrario: hunting attivo su un database di 10 milioni di figure professionali, contatto diretto, selezione reale da parte di un recruiter Lovac prima che tu incontri chiunque. Aggiungi la dashboard in tempo reale e la garanzia sul risultato: il modello è strutturalmente diverso.",
  },
  {
    q: "Quanto costa?",
    a: "Lavoriamo a success fee, una percentuale sulla RAL della figura inserita. Non paghi nulla fino all'inserimento. L'unica eccezione è un acconto iniziale, che viene rimborsato integralmente se non ti presentiamo un numero minimo di candidati in linea con quello che cerchi. Per avere una stima precisa sul tuo caso, prenota una call: è gratuita e senza impegno.",
  },
  {
    q: "Cosa succede se non trovate la figura che cerco?",
    a: "Se non ti presentiamo candidati coerenti con il brief concordato, l'acconto viene rimborsato. Interamente. Non è una clausola nascosta, è il modo in cui scegliamo di lavorare. Se non portiamo risultati, non ha senso che tu paghi.",
  },
  {
    q: "Quanto tempo ci vuole?",
    a: "I primi candidati arrivano entro 15 giorni dall'apertura della ricerca. Il tempo di chiusura dipende dalla complessità del ruolo e dalla disponibilità del cliente per i colloqui. La media sui nostri casi è di circa 30 giorni.",
  },
  {
    q: "Lavorate su tutti i settori?",
    a: "Sì. lavoriamo su tutti i settori, in Italia e all'estero. Il nostro metodo di hunting funziona indipendentemente dal comparto perché si basa sui profili delle persone, non sulle specificità tecniche del settore. Quando serve un profilo con competenze molto verticali, ci avvaliamo di specialisti per ulteriori colloqui tecnici.",
  },
];

export default function Home() {
  const [cases, setCases] = useState([]);

  useEffect(() => {
    listCaseStudies().then((data) => setCases(data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <>
      <Seo
        title="Lovac, Head hunting tech-driven per PMI italiane"
        description="Trovi figure senior, tecnici specializzati e manager in meno di 30 giorni. Hunting attivo su 10 milioni di profili verificati, AI + recruiter dedicati. Paghi solo se funziona."
        path="/"
        jsonLd={{ "@context": "https://schema.org", "@graph": [ORG_JSONLD, WEBSITE_JSONLD] }}
      />
      {/* 1. HERO */}
      <section className="relative bg-lovac-dark text-on-dark overflow-hidden grain">
        <div className="container-lovac px-6 pt-24 pb-20 relative z-10">
          <span className="badge-on-dark reveal">Recruiting · Tech-driven · Italia</span>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 text-on-dark max-w-5xl reveal reveal-1">
            Troviamo i <RotatingWord />
            <br />
            che cerchi da mesi.
          </h1>
          <p className="text-on-dark-muted text-lg mt-8 max-w-2xl reveal reveal-2">
            Un'agenzia di head hunting moderna, in un settore vecchio. Hunting attivo su 10 milioni di figure professionali. Primi candidati in 15 giorni. Paghi solo se funziona.
          </p>
          <div className="flex flex-wrap gap-3 mt-10 reveal reveal-3">
            <Link to="/hiring-scan" className="btn-primary" data-testid={HOME.heroCtaPrimary}>
              Prova l'Hiring Scan <ArrowRight size={16} />
            </Link>
            <Link to="/contatti" className="btn-outline-dark" data-testid={HOME.heroCtaSecondary}>
              Prenota una call
            </Link>
          </div>
        </div>

        {/* 2. STATS BAR */}
        <div className="border-t border-lovac-dark relative z-10">
          <div className="container-lovac px-6 py-14 grid grid-cols-2 lg:grid-cols-4 gap-10">
            <Stat num="+500" label="Figure reclutate" />
            <Stat num="+300" label="Aziende aiutate" />
            <Stat num="< 30 g" label="Time to hire medio" />
            <Stat num="+30" label="Persone nel team" />
          </div>
        </div>
      </section>

      {/* 3. IL METODO */}
      <section className="section bg-lovac">
        <div className="container-lovac">
          <div className="max-w-3xl">
            <p className="badge-lovac">Il metodo</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 text-lovac-dark">
              Non aspettiamo le candidature migliori.<br />
              <span className="text-lovac-muted">Le andiamo a cercare.</span>
            </h2>
            <p className="text-lovac-muted mt-5 text-base max-w-2xl">
              Le agenzie tradizionali lavorano su chi si candida.<br />
              Noi lavoriamo su chi vale la pena trovare.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 mt-14">
            {METHOD.map((m, i) => {
              const Icon = m.icon;
              return (
                <div key={m.title} className="card-lovac reveal" style={{ animationDelay: `${i * 80}ms` }} data-testid={HOME.methodCard}>
                  <div className="flex items-center justify-between mb-6">
                    <div className="h-10 w-10 rounded-lg bg-lovac-2 flex items-center justify-center border-lovac">
                      <Icon size={18} className="text-lovac-dark" />
                    </div>
                    <span className="badge-lovac">{m.badge}</span>
                  </div>
                  <h3 className="font-display text-xl text-lovac-dark mb-3 leading-tight">{m.title}</h3>
                  <p className="text-sm text-lovac-muted leading-relaxed">{m.body}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-10">
            <Link to="/metodo" className="link-underline text-sm text-lovac-dark inline-flex items-center gap-2">
              Vedi il metodo completo <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* 4. CONFRONTO */}
      <section className="section bg-lovac-2">
        <div className="container-lovac">
          <div className="max-w-3xl">
            <p className="badge-lovac">Il confronto</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 text-lovac-dark">
              Un'agenzia di head hunting moderna,<br />
              <span className="text-lovac-muted">in un settore vecchio.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5 mt-14">
            <div className="col-old">
              <p className="text-[11px] uppercase tracking-wider text-lovac-muted mb-5">Agenzie tradizionali</p>
              <ul className="space-y-4">
                {OLD.map((t, i) => (
                  <li
                    key={t}
                    className={`flex items-start gap-3 text-sm text-lovac-dark pb-4 ${
                      i < OLD.length - 1 ? "border-b border-lovac" : ""
                    } ${i === OLD.length - 1 ? "opacity-80" : ""}`}
                  >
                    <X size={18} className="text-lovac-muted shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-new">
              <p className="text-[11px] uppercase tracking-wider text-lovac-accent mb-5">Lovac</p>
              <ul className="space-y-4">
                {NEW.map((t, i) => (
                  <li
                    key={t}
                    className={`flex items-start gap-3 text-sm pb-4 ${
                      i < NEW.length - 1 ? "border-b border-lovac-dark" : ""
                    } ${i === NEW.length - 1 ? "font-medium" : ""} text-on-dark`}
                  >
                    <Check size={18} className="text-lovac-accent shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 5. CASE STUDIES */}
      <section className="section bg-lovac">
        <div className="container-lovac">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div className="max-w-2xl">
              <p className="badge-lovac">Risultati reali</p>
              <h2 className="font-display text-4xl sm:text-5xl mt-4 text-lovac-dark">
                Chi ha già smesso di aspettare.
              </h2>
              <p className="text-lovac-muted mt-4">
                Aziende reali. Risultati misurabili. Nomi e cognomi.
              </p>
            </div>
            <Link to="/casi-studio" className="link-underline text-sm">
              Vedi tutti i casi studio →
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {cases.map((c) => (
              <CaseCard key={c.slug} c={c} compact />
            ))}
          </div>
        </div>
      </section>

      {/* 6. HIRING SCAN TEASER */}
      <section className="bg-lovac-dark text-on-dark py-24 grain relative overflow-hidden">
        <div className="container-lovac px-6 relative z-10">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-center">
            <div>
              <span className="badge-on-dark">Hiring Scan</span>
              <h2 className="font-display text-4xl sm:text-5xl mt-4 text-on-dark">
                Quanto è difficile trovare la figura che cerchi?
              </h2>
              <p className="text-on-dark-muted mt-5 text-base max-w-2xl">
                Inserisci ruolo, seniority e area geografica. Ottieni subito un report gratuito con difficulty score, profili disponibili nel mercato e RAL media di settore.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/hiring-scan" className="btn-primary" data-testid={HOME.scanSubmit}>
                  Prova l'Hiring Scan <ArrowRight size={16} />
                </Link>
                <p className="text-sm text-on-dark">
                  Nessun impegno. Report <strong className="font-semibold text-lovac-accent">generato in 60 secondi</strong>.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-br from-[rgba(0,196,140,0.15)] via-transparent to-transparent rounded-3xl blur-3xl" />
              <img
                src="/images/home-scan-mockup.png"
                alt="Esempio di report Hiring Scan Lovac su laptop"
                className="relative w-full h-auto"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 8. FAQ */}
      <section className="section bg-lovac">
        <div className="container-narrow">
          <p className="badge-lovac">FAQ</p>
          <h2 className="font-display text-4xl sm:text-5xl mt-4 text-lovac-dark">
            Domande frequenti
          </h2>

          <Accordion type="single" collapsible className="mt-10 divide-y divide-[rgba(0,0,0,0.08)] border-t border-b border-[rgba(0,0,0,0.08)]">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`q-${i}`} className="border-0">
                <AccordionTrigger
                  data-testid={`home-faq-trigger-${i}`}
                  className="text-left text-lg sm:text-xl font-display text-lovac-dark hover:no-underline py-6 hover:text-lovac-accent"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-base text-lovac-muted leading-relaxed pb-6 pr-8">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA finale */}
      <section className="section bg-lovac-dark text-on-dark grain relative overflow-hidden">
        <div className="container-narrow text-center relative z-10">
          <span className="badge-on-dark">Prenota una call</span>
          <h2 className="font-display text-4xl sm:text-5xl mt-6 text-on-dark leading-tight">
            Vuoi una mano<br />con la tua ricerca?
          </h2>
          <p className="text-on-dark-muted mt-5 text-base max-w-xl mx-auto">
            Parlacene in una call di 30 minuti. Se non facciamo al caso tuo, te lo diremo.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-10">
            <Link to="/contatti" className="btn-primary">
              Prenota una call <ArrowRight size={16} />
            </Link>
            <Link to="/hiring-scan" className="btn-outline-dark">
              Prova l'Hiring Scan <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
